// Hooks de solo lectura de «Catálogo de productos»; SureKeep no da de alta ni edita (copia de Zentral).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getInventoryItem,
    listInventoryByProduct,
    listProducts,
    listStoreInventory,
    type CatalogInventoryItem,
    type CatalogProduct,
    type InventoryStorePieces,
    type StoreCatalogItem,
} from '@/api/services/retail-catalog/retail-catalog.client';

const KEYS = {
    products: (retailerId: string) => ['retail', 'products', retailerId] as const,
    inventory: (retailerId: string, storeId: string) =>
        ['retail', 'inventory', retailerId, storeId] as const,
    pieces: (retailerId: string, sku: string) =>
        ['retail', 'inventoryByProduct', retailerId, sku] as const,
    item: (retailerId: string, identifier: string) =>
        ['retail', 'inventoryItem', retailerId, identifier] as const,
};

/** Productos del negocio. */
export function useProducts(retailerId: string) {
    return useQuery({
        queryKey: KEYS.products(retailerId),
        queryFn: () => listProducts(retailerId),
        enabled: !!retailerId,
    });
}

/** Fila por SKU de una tienda. */
export function useStoreInventory(retailerId: string, storeId: string) {
    return useQuery({
        queryKey: KEYS.inventory(retailerId, storeId),
        queryFn: () => listStoreInventory(retailerId, storeId),
        enabled: !!retailerId && !!storeId,
    });
}

/** Piezas de un SKU por tienda; `sku` null no consulta. */
export function useInventoryByProduct(retailerId: string, sku: string | null) {
    return useQuery({
        queryKey: KEYS.pieces(retailerId, sku ?? ''),
        queryFn: () => listInventoryByProduct(retailerId, sku ?? ''),
        enabled: !!retailerId && !!sku,
    });
}

/** Ficha de una pieza por IMEI o serie; `identifier` null no consulta. */
export function useInventoryItem(retailerId: string, identifier: string | null) {
    return useQuery({
        queryKey: KEYS.item(retailerId, identifier ?? ''),
        queryFn: () => getInventoryItem(retailerId, identifier ?? ''),
        enabled: !!retailerId && !!identifier,
        retry: false,
    });
}

export type { CatalogInventoryItem, CatalogProduct, InventoryStorePieces, StoreCatalogItem };

/** Vuelve a pedir los productos del negocio, p. ej. al terminar la revisión contra Zentral. */
export function useRefreshProducts(retailerId: string): () => void {
    const queryClient = useQueryClient();
    return () => {
        void queryClient.invalidateQueries({ queryKey: KEYS.products(retailerId) });
    };
}
